import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CustomerNavigationOverlay from '../../components/navigation/CustomerNavigationOverlay';
import ARCanvas from './components/ARCanvas';
import DishInfoOverlay from './components/DishInfoOverlay';
import ShareModal from './components/ShareModal';

import { useLanguage } from '../../i18n/LanguageContext';
import { trackARUse } from '../../services/dishStatsService';
import ARTutorialOverlay from './components/ARTutorialOverlay';
import { useTenant } from '../../contexts/TenantContext';
import KiriEngineScanLoader from './components/KiriEngineScanLoader';

// Local error boundary so AR errors never bubble up to the global ErrorBoundary
class ARErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, info) {
    console.error('ARViewer error:', error, info);
  }
  render() {
    if (this.state?.hasError) {
      return (
        <div className="fixed inset-0 bg-background flex flex-col items-center justify-center z-50 px-6">
          <div className="text-center max-w-sm">
            <div className="w-16 h-16 mx-auto mb-4 bg-destructive/10 rounded-full flex items-center justify-center">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="var(--color-destructive)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
            </div>
            <h2 className="text-xl font-heading font-semibold text-foreground mb-2">
              Erreur AR
            </h2>
            <p className="text-sm text-muted-foreground mb-6">
              Une erreur s'est produite lors du chargement de la vue AR. Veuillez réessayer.
            </p>
            <button
              onClick={() => { this.setState({ hasError: false }); }}
              className="px-6 py-2.5 bg-primary text-primary-foreground rounded-full text-sm font-medium mr-3"
            >
              Réessayer
            </button>
            <button
              onClick={() => { window.history?.back(); }}
              className="px-6 py-2.5 border border-border text-foreground rounded-full text-sm font-medium"
            >
              Retour
            </button>
          </div>
        </div>
      );
    }
    return this.props?.children;
  }
}

const ARViewer = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const { restaurantId: tenantRestaurantId } = useTenant();
  const dishData = location?.state?.dish;
  const modelViewerRef = useRef(null);

  // Guard: tutorial may only be shown once per session
  const hasShownTutorialRef = useRef(false);

  // Guard: once arReady is true it must NEVER go back to false.
  // iOS Quick Look fires a second 'load' event when it closes; without this guard
  // the overlay tree unmounts/remounts and the tutorial loop restarts.
  const arReadyRef = useRef(false);

  // Guard: suppress handleLoadComplete while an AR session (Quick Look / Scene Viewer)
  // is active. iOS fires 'load' again the moment Quick Look dismisses.
  const isARSessionActiveRef = useRef(false);

  const [arReady, setArReady] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [showKiriLoader, setShowKiriLoader] = useState(false);
  const [kiriModelUrl, setKiriModelUrl] = useState(null);

  const mockDish = {
    id: 1,
    name: "Grilled Salmon with Asparagus",
    price: "24.99",
    category: "Main Course",
    servingSize: "Serves 1",
    calories: 450,
    prepTime: "25 mins",
    arUrl: null,
    image: "https://images.unsplash.com/photo-1560717845-968823efbee1",
    imageAlt: "Perfectly grilled salmon fillet with golden-brown sear marks served alongside fresh green asparagus spears on white ceramic plate"
  };

  const currentDish = dishData || mockDish;

  // Use Kiri model URL if loaded, otherwise fall back to dish data
  const activeModelUrl = kiriModelUrl || currentDish?.arUrl || currentDish?.modelUrl;

  // Track AR use when the viewer loads with a dish
  useEffect(() => {
    if (dishData?.id && dishData?.name) {
      const resolvedRestaurantId = dishData?.restaurantId || tenantRestaurantId || null;
      trackARUse(dishData?.id, dishData?.name, resolvedRestaurantId);
    }
  }, [dishData?.id]);

  const handleBack = () => {
    navigate('/restaurant-menu-landing', {
      state: { fromAR: true }
    });
  };

  const handleLoadComplete = useCallback(() => {
    // If an AR session (Quick Look / Scene Viewer) is currently active, iOS will
    // fire 'load' again when the session closes. Ignore it completely.
    if (isARSessionActiveRef?.current) return;

    // Once arReady is true, never process another load event — prevents the
    // iOS re-fire loop that remounts overlays and restarts the tutorial.
    if (arReadyRef?.current) return;

    arReadyRef.current = true;
    setArReady(true);

    if (!hasShownTutorialRef?.current) {
      hasShownTutorialRef.current = true;
      setShowTutorial(true);
    }
  }, []);

  const handleLoadError = useCallback((error) => {
    console.error('AR model loading error:', error);
  }, []);

  // Called by ARCanvas whenever the native AR session starts or ends
  const handleARSessionChange = useCallback((isActive) => {
    isARSessionActiveRef.current = isActive;
  }, []);

  const handleKiriModelLoaded = ({ url }) => {
    setKiriModelUrl(url);
    setShowKiriLoader(false);
    // Reset so the new model can trigger arReady once
    arReadyRef.current = false;
    setArReady(false);
  };

  return (
    <ARErrorBoundary>
      <div className="ar-viewer-page fixed inset-0 bg-background overflow-hidden">
        {/* SCANN3R Watermark */}
        <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden">
          {[-30, 10, 50, 90]?.map((topPercent, i) => (
            <div
              key={i}
              className="absolute w-full flex items-center justify-center select-none"
              style={{
                top: `${topPercent}%`,
                transform: 'rotate(-35deg)',
                transformOrigin: 'center center',
                whiteSpace: 'nowrap',
              }}
            >
              <span className="font-bold tracking-widest" style={{ fontSize: '3.5rem', opacity: 0.15, letterSpacing: '0.2em' }}>
                <span style={{ color: '#b0b0b0' }}>SCANN</span><span style={{ color: '#7dd3fc' }}>3R</span>
              </span>
            </div>
          ))}
        </div>
        <ARCanvas
          modelUrl={activeModelUrl}
          rotation={0}
          scale={1}
          onLoadComplete={handleLoadComplete}
          onLoadError={handleLoadError}
          onARSessionChange={handleARSessionChange}
          modelViewerRef={modelViewerRef}
          dish={currentDish}
        />
        {arReady &&
        <>
            {showTutorial && (
              <ARTutorialOverlay onDismiss={() => setShowTutorial(false)} />
            )}

            <CustomerNavigationOverlay
            onBack={handleBack}
            onClose={handleBack}
            onViewAR={() => {}}
            showBackButton={true}
            showCloseButton={false}
            position="top" />

            <DishInfoOverlay
            dishName={currentDish?.name}
            price={currentDish?.price}
            category={currentDish?.category}
            servingSize={currentDish?.servingSize}
            prepTime={currentDish?.prepTime}
            calories={currentDish?.calories} />

            {/* Kiri Engine load button */}
            <button
              onClick={() => setShowKiriLoader(true)}
              className="absolute bottom-24 right-4 z-20 flex items-center gap-2 px-3 py-2 bg-card/90 backdrop-blur-sm border border-border rounded-xl shadow-warm text-xs font-medium text-foreground hover:bg-card transition-colors"
              title="Load 3D scan from Kiri Engine"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
              Kiri Engine
            </button>
          </>
        }
        <ShareModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          dishName={currentDish?.name}
          screenshotUrl={null} />

        {showKiriLoader && (
          <KiriEngineScanLoader
            onModelLoaded={handleKiriModelLoaded}
            onClose={() => setShowKiriLoader(false)}
          />
        )}
      </div>
    </ARErrorBoundary>
  );
};

export default ARViewer;